import { useEffect, useRef, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  className?: string;
  /** stagger delay step: 1–4 */
  delay?: 1 | 2 | 3 | 4;
  as?: keyof JSX.IntrinsicElements;
};

/** Fades + lifts content into view once it enters the viewport. */
export function Reveal({ children, className = "", delay, as = "div" }: Props) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const Tag = as as "div";
  const cls = ["reveal", delay ? `d${delay}` : "", className].filter(Boolean).join(" ");
  return (
    <Tag ref={ref as React.Ref<HTMLDivElement>} className={cls}>
      {children}
    </Tag>
  );
}
